/**
 * TrustedDevices.tsx — Allows authenticated users to view and revoke
 * their own trusted devices (the 20-day "remember this device" sessions).
 * Displayed as a tab inside ProfilePage.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Laptop } from 'lucide-react';
import { apiClient } from '../../packages/api-client';

interface TrustedDevice {
    id: string;
    device_name: string;
    created_at: string;
    expires_at: string;
    last_used_at?: string;
}

function relativeDate(iso: string) {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return `${diff} days ago`;
}

function expiresIn(iso: string) {
    const d = new Date(iso);
    // ceil, not round: a live device with <12h left must not read as Expired.
    const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
    if (days <= 0) return 'Expired';
    if (days === 1) return 'Expires tomorrow';
    return `Expires in ${days} days`;
}

export function TrustedDevices() {
    const [devices, setDevices] = useState<TrustedDevice[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [revoking, setRevoking] = useState<string | null>(null);

    useEffect(() => {
        apiClient.get<{ data: TrustedDevice[] }>('/auth/trusted-devices')
            .then(r => setDevices(r.data.data ?? []))
            .catch(() => setError('Could not load trusted devices'))
            .finally(() => setLoading(false));
    }, []);

    async function handleRevoke(id: string) {
        if (!confirm("Remove this trusted device? You'll need to enter your OTP on next login from this device.")) return;
        setRevoking(id);
        try {
            await apiClient.delete(`/auth/trusted-devices/${id}`);
            setDevices(prev => prev.filter(d => d.id !== id));
        } catch {
            alert('Failed to revoke device');
        } finally {
            setRevoking(null);
        }
    }

    if (loading) return <div className="spinner" />;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-md)', fontWeight: 700, margin: 0 }}>
                    <ShieldCheck size={18} aria-hidden="true" /> Trusted Devices
                </h3>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    Devices that bypass OTP for 20 days. Remove any device you don't recognise.
                </p>
            </div>

            {error && <p style={{ color: 'var(--danger)', fontSize: 'var(--font-size-sm)' }}>{error}</p>}

            {devices.length === 0 && !error && (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', padding: 'var(--space-4)', textAlign: 'center', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)' }}>
                    No trusted devices. Check "Remember this device" on the OTP screen to add one.
                </p>
            )}

            {devices.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                    <Laptop size={24} aria-hidden="true" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.device_name || 'Unknown device'}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                            Added {relativeDate(d.created_at)} · {expiresIn(d.expires_at)}
                            {d.last_used_at && ` · Last used ${relativeDate(d.last_used_at)}`}
                        </div>
                    </div>
                    <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--danger)', flexShrink: 0 }}
                        disabled={revoking === d.id}
                        onClick={() => handleRevoke(d.id)}
                    >
                        {revoking === d.id ? '…' : 'Revoke'}
                    </button>
                </div>
            ))}
        </div>
    );
}
